import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getMenuBySlug, trackHit, getImageUrl, apiRequest } from '../utils/api';
import MenuLoadErrorView from '../components/menu/MenuLoadErrorView';
import MenuHeader from '../components/MenuHeader';
import MenuFooter from '../components/MenuFooter';
import BannerSlider from '../components/BannerSlider';
import CategoryGrid from '../components/CategoryGrid';
import VotingModal from '../components/VotingModal';
import CategorySection from '../components/CategorySection';
import ProductDetailModal from '../components/ProductDetailModal';
import CampaignDetailModal from '../components/CampaignDetailModal';
import StoryViewer from '../components/StoryViewer';
import CampaignCard from '../components/CampaignCard';

// Helper: Check if a category is Kampanyalar (case-insensitive, trim whitespace)
const isKampanyalarCategory = (cat) => {
    if (!cat) return false;
    const nameLower = (cat.name || '').toLowerCase().trim();
    const slugLower = (cat.slug || '').toLowerCase().trim();
    return nameLower === 'kampanyalar' || nameLower === 'kampanya' ||
           slugLower === 'kampanyalar' || slugLower === 'kampanya';
};

// Countdown timer component for Nox's Choice
const CountdownTimer = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    
    useEffect(() => {
        if (!endDate) return;
        
        const calculateTimeLeft = () => {
            const now = new Date();
            const end = new Date(endDate);
            const diff = end - now;
            
            if (diff <= 0) {
                return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
            }
            
            return {
                days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((diff % (1000 * 60)) / 1000),
                expired: false
            };
        };
        
        setTimeLeft(calculateTimeLeft());
        const interval = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        
        return () => clearInterval(interval);
    }, [endDate]);
    
    if (!endDate || !timeLeft || timeLeft.expired) return null;
    
    return (
        <div className="flex items-center gap-1.5 text-xs">
            <span className="text-white/60">⏰ Kampanya:</span>
            {timeLeft.days > 0 && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded font-mono font-bold text-white">
                    {timeLeft.days}g
                </span>
            )}
            <span className="bg-white/20 px-1.5 py-0.5 rounded font-mono font-bold text-white">
                {String(timeLeft.hours).padStart(2, '0')}
            </span>
            <span className="text-white/60">:</span>
            <span className="bg-white/20 px-1.5 py-0.5 rounded font-mono font-bold text-white">
                {String(timeLeft.minutes).padStart(2, '0')}
            </span>
            <span className="text-white/60">:</span>
            <span className="bg-white/20 px-1.5 py-0.5 rounded font-mono font-bold text-white">
                {String(timeLeft.seconds).padStart(2, '0')}
            </span>
        </div>
    );
};

// Nox's Choice Card Component
const NoxChoiceCard = ({ product, onClick, showPrice = true, settings = {} }) => {
    if (!product) return null;
    
    return (
        <div className="px-4 pt-4">
            <div className="max-w-7xl mx-auto">
                <div 
                    onClick={onClick}
                    className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-[2px] cursor-pointer group"
                >
                    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl p-4 md:p-5">
                        {/* Badge - Fixed positioning and full text */}
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-300 text-black px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg">
                                <span className="text-base">🏆</span>
                                <span>Sizin Seçiminiz</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Product Image */}
                            <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-amber-500/50">
                                {product.images?.[0]?.image_url ? (
                                    <img
                                        src={getImageUrl(product.images[0].image_url)}
                                        alt={product.name}
                                        width={112}
                                        height={112}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : (
                                    settings?.default_product_image ? (
                                        <img
                                            src={getImageUrl(settings.default_product_image)}
                                            alt={product.name}
                                            className="w-full h-full object-cover opacity-50"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-amber-700 to-yellow-600 flex items-center justify-center text-4xl">
                                            🍽️
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-white font-bold text-lg md:text-xl mb-1 truncate" style={{ fontFamily: 'var(--font-product-title)' }}>
                                    {product.name}
                                </h3>
                                {product.description && (
                                    <p className="text-white/60 text-sm line-clamp-1 mb-2" style={{ fontFamily: 'var(--font-product-description)' }}>
                                        {product.description}
                                    </p>
                                )}
                                
                                {/* Price with discount */}
                                {showPrice && (
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        {product.discount_price ? (
                                            <>
                                                <span className="text-white/40 line-through text-sm">
                                                    {parseFloat(product.price).toFixed(2)} ₺
                                                </span>
                                                <span className="text-green-400 font-bold text-xl">
                                                    {parseFloat(product.discount_price).toFixed(2)} ₺
                                                </span>
                                                <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                                    %{product.discount_percentage}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-amber-400 font-bold text-xl">
                                                {parseFloat(product.price).toFixed(2)} ₺
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Campaign end countdown */}
                                {product.vote?.campaign_end_date && (
                                    <CountdownTimer endDate={product.vote.campaign_end_date} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MenuPage = () => {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const branchId = searchParams.get('branch_id') ? parseInt(searchParams.get('branch_id')) : null;

    const [menuData, setMenuData] = useState(null);
    const [loading, setLoading] = useState(true);
    /** null | { type: 'not_found' } | { type: 'generic', message: string } */
    const [loadError, setLoadError] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState(branchId);
    const [activeVote, setActiveVote] = useState(null);
    const [showVoteModal, setShowVoteModal] = useState(false);
    const [noxChoice, setNoxChoice] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryHistory, setCategoryHistory] = useState([]); // Breadcrumb history
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'category'
    const [settings, setSettings] = useState({}); // Organization settings
    // Category display view mode (per category, stored in localStorage)
    const [categoryViewMode, setCategoryViewMode] = useState(null); // null => use category default
    
    // Story states
    const [stories, setStories] = useState([]);
    const [showAutoStory, setShowAutoStory] = useState(false);
    
    // Session-based story tracking (resets when tab/browser closes)
    const [sessionViewedIds, setSessionViewedIds] = useState(() => {
        try {
            const stored = sessionStorage.getItem('qrmenu_session_viewed_stories');
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    
    const [sessionKnownIds, setSessionKnownIds] = useState(() => {
        try {
            const stored = sessionStorage.getItem('qrmenu_session_known_stories');
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    
    const [storyViewerOpenedThisSession, setStoryViewerOpenedThisSession] = useState(() => {
        return sessionStorage.getItem('qrmenu_story_viewer_opened') === 'true';
    });
    
    // Visitor ID for backend tracking (persists across sessions)
    const [visitorId] = useState(() => {
        const stored = localStorage.getItem('qrmenu_visitor_id');
        if (stored) return stored;
        const newId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('qrmenu_visitor_id', newId);
        return newId;
    });

    // Load category view mode per-category from localStorage
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!selectedCategory) {
            setCategoryViewMode(null);
            return;
        }
        const storageKey = `qrmenu_category_view_mode_${selectedCategory.id}`;
        const stored = localStorage.getItem(storageKey);
        setCategoryViewMode(stored || null); // null => use category.display_type
    }, [selectedCategory?.id]);
    
    // Save session data when it changes
    useEffect(() => {
        sessionStorage.setItem('qrmenu_session_viewed_stories', JSON.stringify(sessionViewedIds));
    }, [sessionViewedIds]);
    
    useEffect(() => {
        sessionStorage.setItem('qrmenu_session_known_stories', JSON.stringify(sessionKnownIds));
    }, [sessionKnownIds]);

    useEffect(() => {
        loadMenu();
        
        // Auto-refresh menu data every 5 seconds to catch settings and campaign/vote changes
        const refreshInterval = setInterval(() => {
            loadMenuSilent();
        }, 5000); // 5 seconds - more frequent to catch settings changes immediately
        
        return () => clearInterval(refreshInterval);
    }, [slug, selectedBranchId]);
    
    // Collect all products from all categories for campaign product name lookup
    // Must be before ALL conditional returns to follow React hooks rules
    const allProducts = useMemo(() => {
        if (!menuData?.categories) return [];
        const productMap = new Map();
        menuData.categories.forEach(cat => {
            if (cat.products && cat.products.length > 0) {
                cat.products.forEach(product => {
                    if (!productMap.has(product.id)) {
                        productMap.set(product.id, product);
                    }
                });
            }
        });
        return Array.from(productMap.values());
    }, [menuData?.categories]);
    
    // Silent refresh - Only updates campaign/vote data to avoid form resets
    const loadMenuSilent = async () => {
        try {
            const response = await getMenuBySlug(slug, selectedBranchId);
            
            // Only update Nox's Choice (campaign winner)
            if (response.data.nox_choice) {
                setNoxChoice(response.data.nox_choice);
            } else {
                setNoxChoice(null);
            }
            
            // Only update active vote data
            if (response.data.organization) {
                loadVotingData(response.data.organization.id);
            }
            
            // Update settings if they changed (force update to catch logo and feature changes)
            if (response.data.settings) {
                const newSettings = response.data.settings;
                setSettings(prev => {
                    // Check if settings actually changed
                    const hasChanged = !prev || JSON.stringify(prev) !== JSON.stringify(newSettings);
                    if (hasChanged) {
                        console.log('⚙️ Settings updated:', {
                            restaurant_logo: newSettings.restaurant_logo,
                            enable_search: newSettings.enable_search,
                            enable_whatsapp: newSettings.enable_whatsapp
                        });
                    }
                    return newSettings;
                });
            }
            
            // Update only campaign-related category data (Kampanyalar)
            // without replacing entire menuData to preserve form states
            setMenuData(prev => {
                if (!prev) return response.data;
                
                // Find and update only the Kampanyalar category products
                const updatedCategories = prev.categories?.map(cat => {
                    if (isKampanyalarCategory(cat)) {
                        const newCat = response.data.categories?.find(
                            c => isKampanyalarCategory(c)
                        );
                        return newCat || cat;
                    }
                    return cat;
                });
                
                return {
                    ...prev,
                    categories: updatedCategories || prev.categories,
                    campaigns: response.data.campaigns ?? prev.campaigns,
                    campaigns_menu: response.data.campaigns_menu ?? response.data.campaigns ?? prev.campaigns_menu,
                    banners: response.data.banners || prev.banners, // Banners might change too
                    restaurant: response.data.restaurant || prev.restaurant, // Update restaurant (logo might change)
                    organization: response.data.organization || prev.organization // Update organization
                };
            });

            // Update stories (for instant story add/remove/toggle)
            if (response.data.organization?.id) {
                loadStoriesSilent(response.data.organization.id);
            }
        } catch (err) {
            console.error('Silent refresh error:', err);
        }
    };

    const loadMenu = async () => {
        setLoading(true);
        setLoadError(null);

        try {
            console.log('📋 Loading menu for slug:', slug);
            const response = await getMenuBySlug(slug, selectedBranchId, true); // forceRefresh: true for faster initial load
            console.log('✅ Menu loaded successfully');
            
            // Hemen data'yı set et - UI'ı bloklamadan
            setMenuData(response.data);

            // Set page title with restaurant name
            if (response.data.restaurant?.name) {
                document.title = `${response.data.restaurant.name} - QR Menü`;
            }
            
            // Send Google Analytics pageview for menu page
            if (typeof window !== 'undefined') {
                // Try gtag (Google Analytics 4) - use page_view event instead of config
                if (window.gtag) {
                    window.gtag('event', 'page_view', {
                        page_path: `/${slug}`,
                        page_title: response.data.restaurant?.name ? `${response.data.restaurant.name} - QR Menü` : 'QR Menü',
                        page_location: window.location.href
                    });
                }
                // Try ga (Universal Analytics - legacy)
                if (window.ga) {
                    window.ga('send', 'pageview', {
                        page: `/${slug}`,
                        title: response.data.restaurant?.name ? `${response.data.restaurant.name} - QR Menü` : 'QR Menü'
                    });
                }
            }
            
            // Set settings from menu data
            if (response.data.settings) {
                console.log('📋 Settings loaded:', {
                    enable_search: response.data.settings.enable_search,
                    enable_whatsapp: response.data.settings.enable_whatsapp,
                    show_prices: response.data.settings.show_prices,
                    restaurant_logo: response.data.settings.restaurant_logo,
                    total_settings: Object.keys(response.data.settings).length
                });
                setSettings(response.data.settings);
            } else {
                console.warn('⚠️ No settings found in menu data');
                setSettings({}); // Set empty object to avoid undefined
            }
            
            // Set Nox's Choice from menu data (has full discount info)
            if (response.data.nox_choice) {
                setNoxChoice(response.data.nox_choice);
            }

            // Loading'i hemen kapat - UI responsive olsun
            setLoading(false);

            // Arka planda paralel olarak yükle (bloklamadan)
            if (response.data.organization) {
                const orgId = response.data.organization.id;
                // Paralel yükleme - UI'ı bloklamaz
                Promise.all([
                    trackHit(orgId, 'menu', 0).catch(err => console.debug('Track hit error:', err)),
                    loadVotingData(orgId).catch(err => console.debug('Load voting error:', err)),
                    loadStories(orgId).catch(err => console.debug('Load stories error:', err))
                ]).catch(err => {
                    console.debug('Background loading error:', err);
                });
            }
        } catch (err) {
            console.error('❌ Menu load error:', err);
            if (err?.code === 'RESTAURANT_NOT_FOUND') {
                setLoadError({ type: 'not_found' });
            } else {
                const raw = (err?.message || 'Menü yüklenirken bir hata oluştu').trim();
                setLoadError({ type: 'generic', message: raw });
            }
            setLoading(false);
        }
    };

    const loadVotingData = async (orgId, autoShowVote = true) => {
        try {
            const response = await apiRequest(`/votes/public/${orgId}`);
            if (response.success) {
                // Check for active voting
                if (response.data.current_vote) {
                    setActiveVote(response.data.current_vote);
                    // Auto-show voting modal if voting is active (priority over stories)
                    if (autoShowVote && response.data.current_vote.phase === 'voting' && !response.data.current_vote.has_voted) {
                        console.log('🗳️ Aktif oylama var, modal açılıyor...');
                        setTimeout(() => setShowVoteModal(true), 1000);
                        return true; // Indicates vote modal will show
                    }
                } else {
                    setActiveVote(null);
                }
            }
        } catch (err) {
            console.error('Load voting data error:', err);
        }
        return false;
    };

    // Load stories - NO AUTO-OPEN on initial load
    const loadStories = async (orgId) => {
        try {
            const response = await apiRequest(`/menu/${orgId}/stories`, {
                headers: { 'X-Visitor-Id': visitorId }
            });
            
            if (response.success && response.data?.length > 0) {
                const storyIds = response.data.map(s => s.id);
                setStories(response.data);
                
                // Update known IDs (for detecting new stories later)
                setSessionKnownIds(prev => [...new Set([...prev, ...storyIds])]);
                
                // NO AUTO-OPEN on initial load - user must click logo
            } else {
                setStories([]);
            }
        } catch (err) {
            console.error('Load stories error:', err);
        }
    };

    // Silent story refresh - ONLY auto-opens for TRULY NEW stories added by admin
    // AND only if viewer has NOT been opened this session
    const loadStoriesSilent = async (orgId) => {
        try {
            const response = await apiRequest(`/menu/${orgId}/stories`, {
                headers: { 'X-Visitor-Id': visitorId }
            });
            
            if (response.success) {
                const newStories = response.data || [];
                const newStoryIds = newStories.map(s => s.id);
                
                // Update stories list first
                setStories(newStories);
                
                // Update known IDs and check for new stories in one go
                if (newStoryIds.length > 0) {
                    setSessionKnownIds(prev => {
                        // Check for TRULY NEW stories BEFORE updating
                        const trulyNewUnviewedIds = newStoryIds.filter(id => 
                            !prev.includes(id) && !sessionViewedIds.includes(id)
                        );
                        
                        // AUTO-OPEN ONLY if conditions are met
                        if (
                            trulyNewUnviewedIds.length > 0 && 
                            !storyViewerOpenedThisSession && 
                            !showAutoStory && 
                            !showVoteModal
                        ) {
                            console.log('🎬 Admin yeni story ekledi, otomatik açılıyor:', trulyNewUnviewedIds);
                            // Use setTimeout to avoid state update during render
                            setTimeout(() => {
                                setShowAutoStory(true);
                                setStoryViewerOpenedThisSession(true);
                                sessionStorage.setItem('qrmenu_story_viewer_opened', 'true');
                            }, 100);
                        }
                        
                        // Return updated known IDs
                        return [...new Set([...prev, ...newStoryIds])];
                    });
                }
            }
        } catch (err) {
            // Silent fail
        }
    };

    // Story view handler - marks story as viewed in session
    const handleStoryView = (storyId) => {
        if (!sessionViewedIds.includes(storyId)) {
            setSessionViewedIds(prev => [...prev, storyId]);
        }
    };

    // Story complete handler - marks story as completed in session
    const handleStoryComplete = (storyId) => {
        if (!sessionViewedIds.includes(storyId)) {
            setSessionViewedIds(prev => [...prev, storyId]);
        }
    };

    const handleBranchChange = (newBranchId) => {
        setSelectedBranchId(newBranchId);
    };

    const handleVoteClick = () => {
        setShowVoteModal(true);
    };

    const handleVoteComplete = () => {
        // Reload voting data after voting
        if (menuData?.organization) {
            loadVotingData(menuData.organization.id);
        }
    };

    const handleProductClick = (product) => {
        console.log('🔍 handleProductClick called:', { 
            productId: product?.id, 
            productName: product?.name,
            hasProduct: !!product,
            product: product 
        });
        setSelectedProduct(product);
        console.log('🔍 setSelectedProduct called, selectedProduct should be:', product);
        // Track product view
        if (menuData?.organization) {
            trackHit(menuData.organization.id, 'product', product.id);
        }
        
        // Update page title for product detail
        if (product?.name && menuData?.restaurant?.name) {
            document.title = `${product.name} - ${menuData.restaurant.name} - QR Menü`;
        }
        
        // Send Google Analytics pageview for product detail
        if (typeof window !== 'undefined') {
            // Try gtag (Google Analytics 4) - use page_view event
            if (window.gtag) {
                window.gtag('event', 'page_view', {
                    page_path: `/${slug}/product/${product.id}`,
                    page_title: product.name ? `${product.name} - ${menuData?.restaurant?.name || ''} - QR Menü` : 'Ürün Detayı',
                    page_location: window.location.href
                });
            }
            // Try ga (Universal Analytics - legacy)
            if (window.ga) {
                window.ga('send', 'pageview', {
                    page: `/${slug}/product/${product.id}`,
                    title: product.name ? `${product.name} - ${menuData?.restaurant?.name || ''} - QR Menü` : 'Ürün Detayı'
                });
            }
        }
    };

    // Refs for scroll targets
    const productsSectionRef = useRef(null);
    const subcategoriesSectionRef = useRef(null);
    const navigationBarRef = useRef(null);

    const handleCategoryClick = (category) => {
        // Track category view
        if (menuData?.organization && category?.id) {
            trackHit(menuData.organization.id, 'category', category.id);
        }
        
        // Add current category to history if exists
        if (selectedCategory) {
            setCategoryHistory(prev => [...prev, selectedCategory]);
        }
        setSelectedCategory(category);
        setViewMode('category');
        
        // Scroll to appropriate section
        setTimeout(() => {
            // Check if category has subcategories (use menuData to check, not state)
            const categorySubcategories = (menuData?.categories || []).filter(c => c.parent_id === category.id);
            const hasSubcategories = categorySubcategories.length > 0;
            
            if (hasSubcategories && subcategoriesSectionRef.current) {
                // Scroll to subcategories section (first subcategory) - with offset to show header
                const subcatTop = subcategoriesSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
                const offset = 20; // Small offset to show "Alt Kategoriler" header
                window.scrollTo({ 
                    top: subcatTop - offset, 
                    behavior: 'smooth' 
                });
            } else if (navigationBarRef.current) {
                // Scroll to navigation bar (breadcrumb area) - slightly above it
                const navBarTop = navigationBarRef.current.getBoundingClientRect().top + window.pageYOffset;
                const offset = 80; // Navigation bar height + some padding
                window.scrollTo({ 
                    top: navBarTop - offset, 
                    behavior: 'smooth' 
                });
            } else if (productsSectionRef.current) {
                // Fallback: scroll to products section
                const productsTop = productsSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
                const offset = 100; // Breadcrumb area + some padding
                window.scrollTo({ 
                    top: productsTop - offset, 
                    behavior: 'smooth' 
                });
            } else {
                // Final fallback: scroll to a reasonable position
                const navHeight = 80;
                window.scrollTo({ top: navHeight, behavior: 'smooth' });
            }
        }, 150); // Small delay to ensure DOM is updated
        
        // Update page title for category
        if (category?.name && menuData?.restaurant?.name) {
            document.title = `${category.name} - ${menuData.restaurant.name} - QR Menü`;
        }
        
        // Send Google Analytics pageview for category
        if (typeof window !== 'undefined') {
            // Try gtag (Google Analytics 4) - use page_view event
            if (window.gtag) {
                window.gtag('event', 'page_view', {
                    page_path: `/${slug}/category/${category.id}`,
                    page_title: category.name ? `${category.name} - ${menuData?.restaurant?.name || ''} - QR Menü` : 'Kategori',
                    page_location: window.location.href
                });
            }
            // Try ga (Universal Analytics - legacy)
            if (window.ga) {
                window.ga('send', 'pageview', {
                    page: `/${slug}/category/${category.id}`,
                    title: category.name ? `${category.name} - ${menuData?.restaurant?.name || ''} - QR Menü` : 'Kategori'
                });
            }
        }
    };

    const handleBackClick = () => {
        if (categoryHistory.length > 0) {
            // Go back to previous category
            const newHistory = [...categoryHistory];
            const previousCategory = newHistory.pop();
            setCategoryHistory(newHistory);
            setSelectedCategory(previousCategory);
        } else {
            // Go back to main view
            setSelectedCategory(null);
            setViewMode('grid');
        }
    };

    const handleHomeClick = () => {
        setSelectedCategory(null);
        setCategoryHistory([]);
        setViewMode('grid');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Get subcategories of selected category
    const getSubcategories = (parentId) => {
        return menuData?.categories?.filter(c => c.parent_id === parentId) || [];
    };

    // Get products of selected category
    const getCategoryProducts = (categoryId) => {
        return menuData?.categories?.find(c => c.id === categoryId)?.products || [];
    };

    // Build breadcrumb path
    const getBreadcrumb = () => {
        const path = [];
        if (categoryHistory.length > 0) {
            path.push(...categoryHistory);
        }
        if (selectedCategory) {
            path.push(selectedCategory);
        }
        return path;
    };

    // ============================================
    // ALL HOOKS MUST BE BEFORE CONDITIONAL RETURNS
    // ============================================
    
    // Parse navbar items from settings
    const navbarItems = useMemo(() => {
        try {
            const items = settings?.navbar_items ? JSON.parse(settings.navbar_items) : null;
            if (items && Array.isArray(items)) {
                return items.filter(item => item.enabled !== false);
            }
        } catch (e) {
            console.error('Error parsing navbar_items:', e);
        }
        // Default items
        return [
            { id: 'home', label: 'Ana Sayfa', icon: 'home', type: 'internal', target: 'home', enabled: true },
            { id: 'categories', label: 'Kategoriler', icon: 'grid', type: 'internal', target: 'categories', enabled: true },
            { id: 'campaigns', label: 'Kampanyalar', icon: 'gift', type: 'internal', target: 'campaigns', enabled: true },
            { id: 'cocktails', label: 'İmza Kokteyller', icon: 'cocktail', type: 'category', target: '', enabled: true },
            { id: 'contact', label: 'Bize Yazın', icon: 'message', type: 'internal', target: 'contact', enabled: true }
        ];
    }, [settings?.navbar_items]);

    // CSS Variables for theme customization - All customizable colors
    const themeStyles = useMemo(() => ({
        // General
        '--primary-color': settings?.primary_color || '#f97316',
        '--secondary-color': settings?.secondary_color || '#1f2937',
        '--accent-color': settings?.accent_color || '#dc2626',
        // Backgrounds
        '--background-color': settings?.background_color || '#f9fafb',
        '--card-bg-color': settings?.card_bg_color || '#ffffff',
        '--modal-bg-color': settings?.modal_bg_color || '#ffffff',
        // Text
        '--text-color': settings?.text_color || '#111827',
        '--text-secondary-color': settings?.text_secondary_color || '#6b7280',
        '--text-muted-color': settings?.text_muted_color || '#9ca3af',
        '--home-categories-title-color': settings?.home_categories_title_color || '#ffffff',
        '--home-categories-subtitle-color': settings?.home_categories_subtitle_color || '#d1d5db',
        // Header
        '--header-bg-color': settings?.header_bg_color || '#ffffff',
        '--header-text-color': settings?.header_text_color || '#111827',
        '--header-border-color': settings?.header_border_color || '#e5e7eb',
        // Navbar
        '--navbar-bg-color': settings?.navbar_bg_color || '#ffffff',
        '--navbar-text-color': settings?.navbar_text_color || '#6b7280',
        '--navbar-active-color': settings?.navbar_active_color || '#f97316',
        '--navbar-border-color': settings?.navbar_border_color || '#e5e7eb',
        // Sidebar
        '--sidebar-bg-color': settings?.sidebar_bg_color || '#ffffff',
        '--sidebar-header-start': settings?.sidebar_header_start || '#f97316',
        '--sidebar-header-end': settings?.sidebar_header_end || '#dc2626',
        '--sidebar-logo-bg': settings?.sidebar_logo_bg || '#ffffff',
        '--sidebar-text-color': settings?.sidebar_text_color || '#374151',
        '--sidebar-hover-color': settings?.sidebar_hover_color || '#f3f4f6',
        '--sidebar-active-color': settings?.sidebar_active_color || '#fff7ed',
        '--sidebar-active-text-color': settings?.sidebar_active_text_color || '#ea580c',
        // Categories
        '--category-bg-color': settings?.category_bg_color || '#ffffff',
        '--category-text-color': settings?.category_text_color || '#111827',
        '--category-border-color': settings?.category_border_color || '#e5e7eb',
        '--category-hover-color': settings?.category_hover_color || '#f9fafb',
        '--category-banner-start-color': settings?.category_banner_start_color || '#f97316',
        '--category-banner-end-color': settings?.category_banner_end_color || '#dc2626',
        // Products
        '--product-bg-color': settings?.product_bg_color || '#ffffff',
        '--product-title-color': settings?.product_title_color || '#111827',
        '--product-price-color': settings?.product_price_color || '#16a34a',
        '--product-description-color': settings?.product_description_color || '#6b7280',
        '--product-border-color': settings?.product_border_color || '#e5e7eb',
        // Discount
        '--discount-badge-bg-color': settings?.discount_badge_bg_color || '#dc2626',
        '--discount-badge-text-color': settings?.discount_badge_text_color || '#ffffff',
        '--discount-price-color': settings?.discount_price_color || '#dc2626',
        '--discount-old-price-color': settings?.discount_old_price_color || '#9ca3af',
        '--campaign-bg-color': settings?.campaign_bg_color || '#fef3c7',
        '--campaign-text-color': settings?.campaign_text_color || '#92400e',
        // Buttons
        '--button-primary-color': settings?.button_primary_color || '#f97316',
        '--button-primary-text-color': settings?.button_primary_text_color || '#ffffff',
        '--button-secondary-color': settings?.button_secondary_color || '#e5e7eb',
        '--button-secondary-text-color': settings?.button_secondary_text_color || '#374151',
        // Likes
        '--like-color': settings?.like_color || '#22c55e',
        '--dislike-color': settings?.dislike_color || '#ef4444',
        '--like-bg-color': settings?.like_bg_color || '#f0fdf4',
        '--dislike-bg-color': settings?.dislike_bg_color || '#fef2f2',
        // Fonts (Genel font tüm metinlere varsayılan, diğerleri özel alanlara)
        '--font-general': `"${settings?.general_font || settings?.font_family || 'Inter'}", sans-serif`,
        '--font-category-title': `"${settings?.category_title_font || settings?.font_family || 'Inter'}", sans-serif`,
        '--font-category-description': `"${settings?.category_description_font || settings?.font_family || 'Inter'}", sans-serif`,
        '--font-product-title': `"${settings?.product_title_font || settings?.font_family || 'Inter'}", sans-serif`,
        '--font-product-description': `"${settings?.product_description_font || settings?.font_family || 'Inter'}", sans-serif`,
        fontFamily: `var(--font-general)`
    }), [settings]);

    // Load Google Fonts dynamically (tüm seçili fontları yükle)
    useEffect(() => {
        const defaultFont = settings?.font_family || 'Inter';
        const fonts = [
            settings?.general_font || defaultFont,
            settings?.category_title_font || defaultFont,
            settings?.category_description_font || defaultFont,
            settings?.product_title_font || defaultFont,
            settings?.product_description_font || defaultFont
        ];
        const uniqueFonts = [...new Set(fonts.map(f => (f || 'Inter').trim()))];
        const fontParam = uniqueFonts.map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700`).join('&');
        const fontUrl = `https://fonts.googleapis.com/css2?${fontParam}&display=swap`;

        let link = document.getElementById('google-font-link');
        if (link) {
            link.href = fontUrl;
        } else {
            link = document.createElement('link');
            link.id = 'google-font-link';
            link.rel = 'stylesheet';
            link.href = fontUrl;
            document.head.appendChild(link);
        }
    }, [settings?.general_font, settings?.category_title_font, settings?.category_description_font, settings?.product_title_font, settings?.product_description_font, settings?.font_family]);

    // CRITICAL: Cleanup all custom scripts when organization changes
    // This ensures no script leakage between different organizations
    const prevOrgIdRef = useRef(null);
    useEffect(() => {
        const currentOrgId = menuData?.organization?.id;
        
        // If organization changed, clean up previous organization's scripts
        if (prevOrgIdRef.current && prevOrgIdRef.current !== currentOrgId) {
            // Remove all scripts/styles from previous organization
            const prevOrgId = prevOrgIdRef.current;
            const prevOrgScripts = document.querySelectorAll(`[data-organization-id="${prevOrgId}"]`);
            prevOrgScripts.forEach(element => element.remove());
            
            // Also remove by ID pattern (in case data-attribute is missing)
            const prevOrgHeadScript = document.getElementById(`custom-head-code-org-${prevOrgId}`);
            if (prevOrgHeadScript) prevOrgHeadScript.remove();
            
            const prevOrgCSS = document.getElementById(`custom-organization-css-org-${prevOrgId}`);
            if (prevOrgCSS) prevOrgCSS.remove();
            
            const prevOrgBodyStart = document.getElementById(`custom-body-start-code-org-${prevOrgId}`);
            if (prevOrgBodyStart) prevOrgBodyStart.remove();
            
            const prevOrgBodyEnd = document.getElementById(`custom-body-end-code-org-${prevOrgId}`);
            if (prevOrgBodyEnd) prevOrgBodyEnd.remove();
            
            // Remove all scripts with prefix
            const prevOrgStartScripts = document.querySelectorAll(`[id^="custom-body-start-script-org-${prevOrgId}"]`);
            prevOrgStartScripts.forEach(script => script.remove());
            
            const prevOrgEndScripts = document.querySelectorAll(`[id^="custom-body-end-script-org-${prevOrgId}"]`);
            prevOrgEndScripts.forEach(script => script.remove());
        }
        
        // Update ref for next render
        prevOrgIdRef.current = currentOrgId;
    }, [menuData?.organization?.id]);

    // Apply custom head code (organization-specific)
    // CRITICAL: Each organization's code is isolated by organization ID
    useEffect(() => {
        const orgId = menuData?.organization?.id;
        if (!settings?.custom_head_code || !orgId) return;

        // Use organization-specific ID to prevent conflicts
        const scriptId = `custom-head-code-org-${orgId}`;
        
        // Remove any existing script for this organization
        const existingScript = document.getElementById(scriptId);
        if (existingScript) {
            existingScript.remove();
        }

        try {
            let codeContent = settings.custom_head_code.trim();
            
            if (!codeContent) return;
            
            console.log('📝 Custom head code yükleniyor, uzunluk:', codeContent.length);
            
            // Check if code contains <script> tags (with regex to find all)
            const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
            const scriptMatches = [...codeContent.matchAll(scriptRegex)];
            
            if (scriptMatches.length > 0) {
                // Multiple script tags found
                scriptMatches.forEach((match, index) => {
                    const fullMatch = match[0]; // Full <script>...</script>
                    const scriptContent = match[1]; // Content inside script
                    const scriptTag = match[0].match(/<script([^>]*)>/i);
                    const attributes = scriptTag ? scriptTag[1] : '';
                    
                    // Create new script element
                    const scriptElement = document.createElement('script');
                    scriptElement.id = `${scriptId}-${index}`;
                    scriptElement.setAttribute('data-organization-id', orgId);
                    
                    // Parse and copy attributes (src, async, defer, etc.)
                    if (attributes) {
                        const attrRegex = /(\w+)(?:=["']([^"']*)["'])?/g;
                        let attrMatch;
                        while ((attrMatch = attrRegex.exec(attributes)) !== null) {
                            const attrName = attrMatch[1];
                            const attrValue = attrMatch[2] || '';
                            if (attrName === 'src') {
                                scriptElement.src = attrValue;
                            } else {
                                scriptElement.setAttribute(attrName, attrValue);
                            }
                        }
                    }
                    
                    // Set content (if not external script)
                    if (!scriptElement.src && scriptContent) {
                        scriptElement.textContent = scriptContent;
                    }
                    
                    console.log(`📝 Script ${index + 1} eklendi:`, {
                        id: scriptElement.id,
                        src: scriptElement.src || 'inline',
                        hasContent: !!scriptElement.textContent
                    });
                    
                    document.head.appendChild(scriptElement);
                });
            } else {
                // No script tags found, treat as raw HTML
                // Try to parse as HTML and extract scripts
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = codeContent;
                const scripts = tempDiv.querySelectorAll('script');
                
                if (scripts.length > 0) {
                    scripts.forEach((script, index) => {
                        const scriptElement = document.createElement('script');
                        scriptElement.id = `${scriptId}-${index}`;
                        scriptElement.setAttribute('data-organization-id', orgId);
                        
                        // Copy all attributes
                        Array.from(script.attributes).forEach(attr => {
                            if (attr.name === 'src') {
                                scriptElement.src = attr.value;
                            } else {
                                scriptElement.setAttribute(attr.name, attr.value);
                            }
                        });
                        
                        // Copy content if not external
                        if (!scriptElement.src && script.textContent) {
                            scriptElement.textContent = script.textContent;
                        }
                        
                        console.log(`📝 Script ${index + 1} eklendi (parsed):`, {
                            id: scriptElement.id,
                            src: scriptElement.src || 'inline'
                        });
                        
                        document.head.appendChild(scriptElement);
                    });
                } else {
                    // No scripts found, inject as-is (might be other HTML)
                    const tempContainer = document.createElement('div');
                    tempContainer.innerHTML = codeContent;
                    while (tempContainer.firstChild) {
                        document.head.appendChild(tempContainer.firstChild);
                    }
                }
            }
        } catch (error) {
            console.error('❌ Error adding custom head code:', error);
        }

        return () => {
            // Cleanup: remove script when component unmounts or organization changes
            const script = document.getElementById(scriptId);
            if (script) {
                script.remove();
            }
        };
    }, [settings?.custom_head_code, menuData?.organization?.id]);

    // Apply custom CSS (organization-specific)
    // CRITICAL: Each organization's CSS is isolated by organization ID
    useEffect(() => {
        const orgId = menuData?.organization?.id;
        if (!settings?.custom_css || !orgId) return;

        // Use organization-specific ID to prevent conflicts
        const styleId = `custom-organization-css-org-${orgId}`;
        
        // Remove any existing style for this organization
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
            existingStyle.remove();
        }

        // Create new style with organization ID as data attribute
        const styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.setAttribute('data-organization-id', orgId);
        styleElement.textContent = settings.custom_css;
        document.head.appendChild(styleElement);

        return () => {
            // Cleanup: remove style when component unmounts or organization changes
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, [settings?.custom_css, menuData?.organization?.id]);

    // Apply custom body start code (organization-specific)
    // CRITICAL: Each organization's code is isolated by organization ID
    useEffect(() => {
        const orgId = menuData?.organization?.id;
        if (!settings?.custom_body_start_code || !orgId) return;

        // Use organization-specific ID to prevent conflicts
        const containerId = `custom-body-start-code-org-${orgId}`;
        const scriptPrefix = `custom-body-start-script-org-${orgId}`;
        
        // Remove any existing container and scripts for this organization
        const existingContainer = document.getElementById(containerId);
        if (existingContainer) {
            existingContainer.remove();
        }
        
        // Remove any existing scripts from previous organization
        const existingScripts = document.querySelectorAll(`[id^="${scriptPrefix}"]`);
        existingScripts.forEach(script => script.remove());

        // Create new container
        const container = document.createElement('div');
        container.id = containerId;
        container.setAttribute('data-organization-id', orgId);
        container.style.display = 'none';
        document.body.insertBefore(container, document.body.firstChild);

        // Parse and execute code
        container.innerHTML = settings.custom_body_start_code;
        
        // Execute any script tags and track them
        const scripts = container.querySelectorAll('script');
        scripts.forEach((oldScript, index) => {
            const newScript = document.createElement('script');
            newScript.id = `${scriptPrefix}-${index}`;
            newScript.setAttribute('data-organization-id', orgId);
            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            document.body.appendChild(newScript);
            oldScript.remove();
        });

        return () => {
            // Cleanup: remove container and all scripts for this organization
            const containerToRemove = document.getElementById(containerId);
            if (containerToRemove) {
                containerToRemove.remove();
            }
            
            // Remove all scripts for this organization
            const scriptsToRemove = document.querySelectorAll(`[id^="${scriptPrefix}"]`);
            scriptsToRemove.forEach(script => script.remove());
        };
    }, [settings?.custom_body_start_code, menuData?.organization?.id]);

    // Apply custom body end code (organization-specific)
    // CRITICAL: Each organization's code is isolated by organization ID
    useEffect(() => {
        const orgId = menuData?.organization?.id;
        if (!settings?.custom_body_end_code || !orgId) return;

        // Use organization-specific ID to prevent conflicts
        const containerId = `custom-body-end-code-org-${orgId}`;
        const scriptPrefix = `custom-body-end-script-org-${orgId}`;
        
        // Remove any existing container and scripts for this organization
        const existingContainer = document.getElementById(containerId);
        if (existingContainer) {
            existingContainer.remove();
        }
        
        // Remove any existing scripts from previous organization
        const existingScripts = document.querySelectorAll(`[id^="${scriptPrefix}"]`);
        existingScripts.forEach(script => script.remove());

        // Create new container
        const container = document.createElement('div');
        container.id = containerId;
        container.setAttribute('data-organization-id', orgId);
        container.style.display = 'none';
        document.body.appendChild(container);

        // Parse and execute code
        container.innerHTML = settings.custom_body_end_code;
        
        // Execute any script tags and track them
        const scripts = container.querySelectorAll('script');
        scripts.forEach((oldScript, index) => {
            const newScript = document.createElement('script');
            newScript.id = `${scriptPrefix}-${index}`;
            newScript.setAttribute('data-organization-id', orgId);
            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            document.body.appendChild(newScript);
            oldScript.remove();
        });

        return () => {
            // Cleanup: remove container and all scripts for this organization
            const containerToRemove = document.getElementById(containerId);
            if (containerToRemove) {
                containerToRemove.remove();
            }
            
            // Remove all scripts for this organization
            const scriptsToRemove = document.querySelectorAll(`[id^="${scriptPrefix}"]`);
            scriptsToRemove.forEach(script => script.remove());
        };
    }, [settings?.custom_body_end_code, menuData?.organization?.id]);

    // ============================================
    // CONDITIONAL RETURNS (AFTER ALL HOOKS)
    // ============================================

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 text-lg">Menü yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <MenuLoadErrorView
                variant={loadError.type}
                message={loadError.type === 'generic' ? loadError.message : undefined}
                onRetry={loadMenu}
            />
        );
    }

    if (!menuData) {
        return null;
    }

    const { organization, restaurant, branches, banners, campaigns, campaigns_menu, categories } = menuData;
    const campaignsForMenuList = campaigns_menu ?? campaigns;

    const allCategories = categories || [];

    // Helper function to check if a setting is enabled (defaults to true if not set)
    const isSettingEnabled = (key, defaultValue = true) => {
        if (!settings || Object.keys(settings).length === 0) {
            return defaultValue;
        }
        const value = settings[key];
        if (value === undefined || value === null) {
            return defaultValue;
        }
        // Handle string 'true'/'false' and boolean true/false
        if (value === 'true' || value === true) return true;
        if (value === 'false' || value === false) return false;
        return defaultValue;
    };

    // Get setting value with default (inline function, not a hook)
    const getSettingValue = (key, defaultValue = '') => {
        return settings?.[key] || defaultValue;
    };

    // Helper to get campaign count for Kampanyalar category (only active ones)
    const getCategoryProductCount = (category) => {
        if (isKampanyalarCategory(category)) {
            // Menü Kampanyalar listesi (saat ayarına göre backend ayrı filtreler)
            const campaignCount = (menuData?.campaigns_menu ?? menuData?.campaigns)?.length || 0;
            // Count only active discounted products (already filtered by backend)
            const productCount = category.products?.length || 0;
            return campaignCount + productCount;
        }
        return category.products?.length || 0;
    };

    // Override category products length for display
    const displayCategories = allCategories.map(cat => ({
        ...cat,
        displayCount: getCategoryProductCount(cat)
    }));
    
    const mainCategories = displayCategories.filter(c => !c.parent_id) || [];

    return (
        <div className="min-h-screen flex flex-col" style={{ ...themeStyles, backgroundColor: 'var(--background-color)', color: 'var(--text-color)' }}>
            {/* Header */}
            <MenuHeader
                restaurant={restaurant}
                organization={organization}
                branches={branches}
                onBranchChange={handleBranchChange}
                selectedBranchId={selectedBranchId}
                categories={displayCategories}
                onProductClick={handleProductClick}
                onCategoryClick={handleCategoryClick}
                settings={settings}
                isSettingEnabled={isSettingEnabled}
                stories={stories}
                viewedStoryIds={sessionViewedIds}
                onStoryView={handleStoryView}
                onStoryComplete={handleStoryComplete}
                visitorId={visitorId}
                onHomeClick={handleHomeClick}
            />

            {/* Main Content */}
            <main className="flex-1 pb-20 md:pb-8">
                {/* Nox's Choice - Winner Product */}
                {noxChoice && isSettingEnabled('enable_voting', true) && (
                    <NoxChoiceCard 
                        product={noxChoice} 
                        onClick={() => handleProductClick(noxChoice)}
                        showPrice={isSettingEnabled('show_prices', true)}
                        settings={settings}
                    />
                )}

                {/* Active Voting Banner */}
                {activeVote && isSettingEnabled('enable_voting', true) && (
                    <div className="px-4 pt-4">
                        <div className="max-w-7xl mx-auto">
                            <div 
                                onClick={handleVoteClick}
                                className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all group ${
                                    activeVote.phase === 'voting' 
                                        ? 'bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 animate-gradient-x' 
                                        : 'bg-gradient-to-r from-green-600 to-emerald-600'
                                }`}
                            >
                                {/* Animated background */}
                                <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10 animate-shimmer" />
                                
                                <div className="relative p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-2xl animate-bounce">🗳️</span>
                                                <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                                                    {activeVote.phase === 'voting' ? 'OYLAMA AÇIK' : 'SONUÇLAR'}
                                                </span>
                                            </div>
                                            <h3 className="text-white font-bold text-xl">{activeVote.title}</h3>
                                            {activeVote.description && (
                                                <p className="text-white/80 text-sm mt-1">{activeVote.description}</p>
                                            )}
                                            
                                            {activeVote.has_voted && (
                                                <div className="mt-2 text-green-200 text-sm flex items-center gap-1">
                                                    <span>✓</span> Oy kullandınız
                                        </div>
                                            )}
                                        </div>
                                        
                                        <div className="text-center ml-4">
                                            {/* Product previews */}
                                            <div className="flex -space-x-3 justify-center mb-2">
                                                {activeVote.options?.slice(0, 3).map((opt, idx) => (
                                                    <div 
                                                        key={opt.id}
                                                        className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg bg-white"
                                                        style={{ zIndex: 3 - idx }}
                                                    >
                                                        {opt.product_image ? (
                                                            <img
                                                                src={getImageUrl(opt.product_image)}
                                                                alt={opt.product_name}
                                                                className="w-full h-full object-cover"
                                                                loading="lazy"
                                                                decoding="async"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-lg">
                                                                🍽️
                                    </div>
                                                        )}
                                </div>
                            ))}
                                                {activeVote.options?.length > 3 && (
                                                    <div className="w-12 h-12 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-white text-sm font-bold">
                                                        +{activeVote.options.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {activeVote.phase === 'voting' && !activeVote.has_voted && (
                                                <div className="bg-white text-purple-600 font-bold px-4 py-2 rounded-full text-sm group-hover:scale-105 transition">
                                                    Oy Ver →
                                                </div>
                                            )}
                                            {activeVote.has_voted && (
                                                <div className="bg-white/20 text-white px-4 py-2 rounded-full text-sm">
                                                    Sonuçları Gör →
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Banner Slider */}
                {banners && banners.length > 0 && (
                    <div className="px-4 pt-4">
                        <div className="max-w-7xl mx-auto">
                            <BannerSlider banners={banners} />
                        </div>
                    </div>
                )}

                {/* Category Navigation - Mobile App Style */}
                {viewMode === 'category' && selectedCategory ? (
                    <div className="px-4 pt-4">
                        <div className="max-w-7xl mx-auto">
                            {/* Navigation Bar */}
                            <div ref={navigationBarRef} className="flex items-center justify-between mb-4 bg-white rounded-xl shadow-sm p-3 sticky top-16 z-30">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleBackClick}
                                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={handleHomeClick}
                                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                        </svg>
                                    </button>
                                </div>
                                
                                {/* Breadcrumb */}
                                <div className="flex-1 overflow-x-auto ml-3">
                                    <div className="flex items-center gap-1 text-sm whitespace-nowrap">
                                        <button 
                                            onClick={handleHomeClick}
                                            className="text-gray-500 hover:text-orange-600 transition"
                                        >
                                            Menü
                                        </button>
                                        {getBreadcrumb().map((cat, index) => (
                                            <span key={cat.id} className="flex items-center gap-1">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                <button
                                                    onClick={() => {
                                                        const newHistory = categoryHistory.slice(0, index);
                                                        setCategoryHistory(newHistory);
                                                        setSelectedCategory(cat);
                                                    }}
                                                    className={`${
                                                        index === getBreadcrumb().length - 1 
                                                            ? 'text-orange-600 font-semibold' 
                                                            : 'text-gray-500 hover:text-orange-600'
                                                    } transition`}
                                                >
                                                    {cat.name}
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Category Header */}
                            <div 
                                className="rounded-2xl p-6 mb-6 text-white shadow-lg relative overflow-hidden"
                                style={{
                                    background: `linear-gradient(to right, var(--category-banner-start-color, #f97316), var(--category-banner-end-color, #dc2626))`
                                }}
                            >
                                {/* Category Image Background (if exists) */}
                                {selectedCategory.image && (
                                    <div className="absolute inset-0 opacity-20">
                                        <img 
                                            src={getImageUrl(selectedCategory.image)} 
                                            alt={selectedCategory.name}
                                            width={800}
                                            height={200}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                )}
                                <div className="flex items-start justify-between gap-4 relative z-10">
                                    <div className="flex-1">
                                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-category-title)' }}>{selectedCategory.name}</h2>
                                {selectedCategory.description && (
                                    <p className="text-white/90 text-sm" style={{ fontFamily: 'var(--font-category-description)' }}>{selectedCategory.description}</p>
                                )}
                                        {!isKampanyalarCategory(selectedCategory) && getCategoryProducts(selectedCategory.id).length > 0 && (
                                    <div className="mt-2 text-sm text-white/80" style={{ fontFamily: 'var(--font-general)' }}>
                                        {getCategoryProducts(selectedCategory.id).length} ürün
                                    </div>
                                )}
                                        {isKampanyalarCategory(selectedCategory) && (
                                            <div className="mt-2 text-sm text-white/80" style={{ fontFamily: 'var(--font-general)' }}>
                                                {(() => {
                                                    // Count all campaigns including Nox's Choice - use EXACT same logic as rendering below
                                                    const allCampaigns = [];
                                                    
                                                    // Add Nox's Choice as first campaign if it has active discount
                                                    // IMPORTANT: Use same check as rendering (line 1571-1587)
                                                    if (noxChoice && noxChoice.is_discount_active && noxChoice.vote?.campaign_end_date) {
                                                        const campaignEndDate = new Date(noxChoice.vote.campaign_end_date);
                                                        const now = new Date();
                                                        // Only add if campaign is still active (not expired) - same check as rendering
                                                        if (campaignEndDate >= now) {
                                                            allCampaigns.push({ id: `nox-choice-${noxChoice.id}` });
                                                        }
                                                    }
                                                    
                                                    if (campaignsForMenuList && campaignsForMenuList.length > 0) {
                                                        allCampaigns.push(...campaignsForMenuList);
                                                    }
                                                    
                                                    // Return count or "Aktif kampanya yok" - same as rendering logic
                                                    return allCampaigns.length > 0 ? `${allCampaigns.length} kampanya` : 'Aktif kampanya yok';
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* View Mode Toggle - Show for Kampanyalar category too */}
                                    {(getCategoryProducts(selectedCategory.id).length > 0 || isKampanyalarCategory(selectedCategory)) && (
                                        <div className="flex gap-1 bg-white/20 backdrop-blur-sm p-1 rounded-lg">
                                            {(() => {
                                                // Per-category storage key
                                                if (!selectedCategory) return null;
                                                const storageKey = `qrmenu_category_view_mode_${selectedCategory.id}`;
                                                return (
                                                    <>
                                            <button
                                                onClick={() => {
                                                    const newMode = 'grid_single';
                                                    setCategoryViewMode(newMode);
                                                    localStorage.setItem(storageKey, newMode);
                                                }}
                                                className={`p-2 rounded transition ${
                                                    (categoryViewMode || selectedCategory.display_type || 'grid_double') === 'grid_single'
                                                        ? 'bg-white text-orange-600 shadow-md'
                                                        : 'text-white/80 hover:bg-white/10'
                                                }`}
                                                title="Tekli Kare"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const newMode = 'grid_double';
                                                    setCategoryViewMode(newMode);
                                                    localStorage.setItem(storageKey, newMode);
                                                }}
                                                className={`p-2 rounded transition ${
                                                    (categoryViewMode || selectedCategory.display_type || 'grid_double') === 'grid_double'
                                                        ? 'bg-white text-orange-600 shadow-md'
                                                        : 'text-white/80 hover:bg-white/10'
                                                }`}
                                                title="İkili Kare"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const newMode = 'list';
                                                    setCategoryViewMode(newMode);
                                                    localStorage.setItem(storageKey, newMode);
                                                }}
                                                className={`p-2 rounded transition ${
                                                    (categoryViewMode || selectedCategory.display_type || 'grid_double') === 'list'
                                                        ? 'bg-white text-orange-600 shadow-md'
                                                        : 'text-white/80 hover:bg-white/10'
                                                }`}
                                                title="Liste"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                                </svg>
                                            </button>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Subcategories - Full Width with Images */}
                            {getSubcategories(selectedCategory.id).length > 0 && (
                                <div ref={subcategoriesSectionRef} className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Alt Kategoriler</h3>
                                    <div className="space-y-3">
                                        {getSubcategories(selectedCategory.id).map(subcat => (
                                            <button
                                                key={subcat.id}
                                                onClick={() => handleCategoryClick(subcat)}
                                                className="w-full group relative overflow-hidden rounded-xl bg-gray-100 hover:shadow-lg transition-all duration-300"
                                            >
                                                {/* Full Width Image */}
                                                <div className="aspect-[3/1] relative">
                                                    {subcat.image ? (
                                                        <img 
                                                            src={getImageUrl(subcat.image)} 
                                                            alt={subcat.name}
                                                            width={600}
                                                            height={200}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                            loading="lazy"
                                                            decoding="async"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Dark Overlay */}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                                    
                                                    {/* Subcategory Info */}
                                                    <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                                                        <div>
                                                            <h4 className="text-white font-bold text-lg md:text-xl">
                                                                {subcat.name}
                                                            </h4>
                                                            {subcat.products?.length > 0 && (
                                                                <p className="text-white/80 text-sm mt-0.5">
                                                                    {subcat.products.length} ürün
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition">
                                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Category Products or Campaigns */}
                            <div ref={productsSectionRef}>
                            {isKampanyalarCategory(selectedCategory) ? (
                                // Show only campaigns (CampaignCard) - no individual products
                                <div className="space-y-6">
                                    {/* Campaigns as CampaignCard - sorted by sort_order from admin panel */}
                                    {(() => {
                                        // Get display type for Kampanyalar category
                                        const displayType = categoryViewMode || selectedCategory.display_type || 'grid_double';
                                        
                                        // Get grid classes based on display type
                                        const getGridClasses = () => {
                                            switch (displayType) {
                                                case 'grid_single':
                                                    return 'grid grid-cols-1 gap-4';
                                                case 'grid_double':
                                                    // 2 kolon hem mobil hem desktop, büyük ekranlarda daha fazla
                                                    return 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4';
                                                case 'list':
                                                    return 'grid grid-cols-1 gap-3';
                                                default:
                                                    return 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4';
                                            }
                                        };
                                        
                                        // Combine campaigns with Nox's Choice (if active and has discount)
                                        const allCampaigns = [];
                                        
                                        // Add Nox's Choice as first campaign if it has active discount
                                        if (noxChoice && noxChoice.is_discount_active && noxChoice.vote?.campaign_end_date) {
                                            const campaignEndDate = new Date(noxChoice.vote.campaign_end_date);
                                            const now = new Date();
                                            // Only add if campaign is still active (not expired)
                                            if (campaignEndDate >= now) {
                                                allCampaigns.push({
                                                    id: `nox-choice-${noxChoice.id}`,
                                                    title: noxChoice.vote.title || 'Sizin Seçiminiz',
                                                    description: `🏆 ${noxChoice.name} - Oylama Kazananı`,
                                                    campaign_type: 'nox_choice',
                                                    campaign_image: noxChoice.images?.[0]?.image_url || noxChoice.image,
                                                    end_date: noxChoice.vote.campaign_end_date,
                                                    sort_order: -1, // Always first
                                                    products: [noxChoice],
                                                    noxChoiceProduct: noxChoice
                                                });
                                            }
                                        }
                                        
                                        if (campaignsForMenuList && campaignsForMenuList.length > 0) {
                                            allCampaigns.push(...campaignsForMenuList);
                                        }
                                        
                                        // Sort by sort_order (Nox's Choice will be first with -1)
                                        allCampaigns.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                                        
                                        return allCampaigns.length > 0 ? (
                                            <div className={getGridClasses()}>
                                                {allCampaigns.map(campaign => {
                                                // Find products for this campaign
                                                let campaignProducts = [];
                                                
                                                // For Nox's Choice campaigns
                                                if (campaign.campaign_type === 'nox_choice' && campaign.noxChoiceProduct) {
                                                    campaignProducts = [campaign.noxChoiceProduct];
                                                }
                                                // For combination campaigns, use combination_rules
                                                else if (campaign.campaign_type === 'combination' && campaign.combination_rules) {
                                                    const campaignProductIds = campaign.combination_rules.map(rule => rule.product_id).filter(Boolean);
                                                    campaignProducts = campaignProductIds
                                                    .map(productId => allProducts.find(p => p.id === productId))
                                                    .filter(Boolean);
                                                }
                                                // For quantity_discount campaigns, use quantity_rules
                                                else if (campaign.campaign_type === 'quantity_discount' && campaign.quantity_rules) {
                                                    const campaignProductIds = campaign.quantity_rules.map(rule => rule.product_id).filter(Boolean);
                                                    campaignProducts = campaignProductIds
                                                        .map(productId => allProducts.find(p => p.id === productId))
                                                        .filter(Boolean);
                                                }
                                                // For discount campaigns, use campaign.products or find products from product_ids
                                                else if (campaign.campaign_type === 'discount') {
                                                    // First try: use campaign.products if available
                                                    if (campaign.products && campaign.products.length > 0) {
                                                        campaignProducts = campaign.products;
                                                    }
                                                    // Tek ürün kampanyası: saat dışında campaign_prices boş olur; product_id ile menüden çöz
                                                    else if (campaign.product_id) {
                                                        const p = allProducts.find(x => x.id === campaign.product_id);
                                                        if (p) campaignProducts = [p];
                                                    }
                                                    // Second try: use product_ids if available
                                                    else if (campaign.product_ids && Array.isArray(campaign.product_ids)) {
                                                        campaignProducts = campaign.product_ids
                                                            .map(productId => allProducts.find(p => p.id === (typeof productId === 'object' ? (productId.id || productId.product_id) : productId)))
                                                            .filter(Boolean);
                                                    }
                                                    // Third try: find products that have this campaign in their campaign_prices (saat içi)
                                                    if (campaignProducts.length === 0) {
                                                        campaignProducts = allProducts.filter(product => {
                                                            if (product.campaign_prices && Array.isArray(product.campaign_prices)) {
                                                                return product.campaign_prices.some(cp => cp.campaign_id === campaign.id);
                                                            }
                                                            return false;
                                                        });
                                                    }
                                                }
                                                // Fallback: use campaign.products if available
                                                else if (campaign.products && campaign.products.length > 0) {
                                                    campaignProducts = campaign.products;
                                                }
                                                
                                                return (
                                                    <CampaignCard
                                                        key={campaign.id}
                                                        campaign={campaign}
                                                        campaignProducts={campaignProducts}
                                                        compact={displayType === 'list'}
                                                        onCampaignClick={() => {
                                                            console.log('Kampanya tıklandı:', campaign.id);
                                                            // Track campaign view
                                                            if (menuData?.organization && campaign?.id) {
                                                                trackHit(menuData.organization.id, 'campaign', campaign.id);
                                                            }
                                                            if (campaign.campaign_type === 'nox_choice' && campaign.noxChoiceProduct) {
                                                                handleProductClick(campaign.noxChoiceProduct);
                                                            } else {
                                                                // Ensure products are included in campaign object
                                                                setSelectedCampaign({
                                                                    ...campaign,
                                                                    products: campaignProducts
                                                                });
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                        ) : (
                                        <div className="text-center py-12">
                                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                            </svg>
                                            <p className="text-gray-500 text-lg">Şu anda aktif kampanya bulunmamaktadır.</p>
                                        </div>
                                    );
                                    })()}
                                </div>
                            ) : (
                                // Show regular products
                            <CategorySection
                                category={selectedCategory}
                                onLike={(productId, liked) => {}}
                                onProductClick={handleProductClick}
                                showHeader={false}
                                    settings={settings}
                                    isSettingEnabled={isSettingEnabled}
                                userViewMode={categoryViewMode}
                            />
                            )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Category Grid - Only show categories, click to navigate */}
                        {mainCategories.length > 0 ? (
                            <div id="categories-section" className="px-4 pt-6">
                                <div className="max-w-7xl mx-auto">
                                    <div className="mb-4">
                                        <h2 
                                            className="text-2xl font-bold"
                                            style={{ color: 'var(--home-categories-title-color, var(--text-color))', fontFamily: 'var(--font-general)' }}
                                        >
                                            Kategoriler
                                        </h2>
                                        <p 
                                            className="text-sm mt-1"
                                            style={{ color: 'var(--home-categories-subtitle-color, var(--text-secondary-color))', fontFamily: 'var(--font-general)' }}
                                        >
                                            Menümüzü keşfedin
                                        </p>
                                    </div>
                                    <CategoryGrid
                                        categories={displayCategories}
                                        onCategoryClick={handleCategoryClick}
                                        settings={settings}
                                        isSettingEnabled={isSettingEnabled}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-16 px-4">
                                <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                                <p className="text-gray-500 text-lg">Henüz kategori eklenmemiş.</p>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Footer */}
            <MenuFooter 
                restaurant={restaurant} 
                organizationId={organization?.id}
                categories={allCategories}
                onCategoryClick={handleCategoryClick}
                onHomeClick={handleHomeClick}
                settings={settings}
                isSettingEnabled={isSettingEnabled}
                navbarItems={navbarItems}
                themeStyles={themeStyles}
            />

            {/* Voting Modal */}
            {showVoteModal && activeVote && isSettingEnabled('enable_voting', true) && (
                <VotingModal
                    vote={activeVote}
                    isOpen={showVoteModal}
                    onClose={() => {
                        setShowVoteModal(false);
                        // NO AUTO-OPEN after closing vote modal
                        // User can click logo to open stories manually
                    }}
                    organizationId={organization?.id}
                    onVoteComplete={handleVoteComplete}
                />
            )}

            {/* Product Detail Modal */}
            <ProductDetailModal
                product={selectedProduct}
                isOpen={!!selectedProduct}
                key={selectedProduct?.id || 'no-product'} // Force re-render when product changes
                onClose={() => {
                    setSelectedProduct(null);
                    // Reset page title to restaurant name when closing product detail
                    if (menuData?.restaurant?.name) {
                        document.title = `${menuData.restaurant.name} - QR Menü`;
                    }
                    // Send Google Analytics pageview for menu page
                    if (typeof window !== 'undefined') {
                        if (window.gtag) {
                            window.gtag('event', 'page_view', {
                                page_path: `/${slug}`,
                                page_title: menuData?.restaurant?.name ? `${menuData.restaurant.name} - QR Menü` : 'QR Menü',
                                page_location: window.location.href
                            });
                        }
                        if (window.ga) {
                            window.ga('send', 'pageview', {
                                page: `/${slug}`,
                                title: menuData?.restaurant?.name ? `${menuData.restaurant.name} - QR Menü` : 'QR Menü'
                            });
                        }
                    }
                }}
                onLike={(productId, liked) => {
                    // Update product in menuData if needed
                }}
                settings={settings}
                isSettingEnabled={isSettingEnabled}
                categoryProducts={selectedProduct ? getCategoryProducts(selectedProduct.category_id) : []}
                onProductChange={(newProduct) => {
                    setSelectedProduct(newProduct);
                    // Track product view on navigation
                    if (menuData?.organization && newProduct) {
                        trackHit(menuData.organization.id, 'product', newProduct.id);
                    }
                }}
                restaurantName={menuData?.restaurant?.name || ''}
                slug={slug}
                campaigns={menuData?.campaigns || []}
                allProducts={allProducts}
            />

            {/* Campaign Detail Modal */}
            <CampaignDetailModal
                campaign={selectedCampaign ? (() => {
                    // Ensure products are populated for discount campaigns
                    let campaignProducts = selectedCampaign.products || [];
                    
                    if (selectedCampaign.campaign_type === 'discount' && (!campaignProducts || campaignProducts.length === 0)) {
                        // Find products from allProducts that have this campaign in their campaign_prices
                        campaignProducts = allProducts.filter(product => {
                            if (product.campaign_prices && Array.isArray(product.campaign_prices)) {
                                return product.campaign_prices.some(cp => cp.campaign_id === selectedCampaign.id);
                            }
                            return false;
                        });
                    }
                    
                    return {
                        ...selectedCampaign,
                        products: campaignProducts
                    };
                })() : null}
                isOpen={!!selectedCampaign}
                onClose={() => setSelectedCampaign(null)}
                onProductClick={(product) => {
                    setSelectedCampaign(null); // Close campaign modal
                    handleProductClick(product); // Open product modal
                }}
            />

            {/* Auto Story Viewer */}
            {showAutoStory && stories.length > 0 && (
                <StoryViewer
                    stories={stories}
                    initialIndex={-1}
                    viewedStoryIds={sessionViewedIds}
                    onClose={() => {
                        setShowAutoStory(false);
                        // Mark that viewer was opened this session - NO MORE AUTO OPEN
                        if (!storyViewerOpenedThisSession) {
                            setStoryViewerOpenedThisSession(true);
                            sessionStorage.setItem('qrmenu_story_viewer_opened', 'true');
                        }
                    }}
                    onStoryView={(storyId) => {
                        handleStoryView(storyId);
                        // Mark that viewer was opened this session - NO MORE AUTO OPEN
                        if (!storyViewerOpenedThisSession) {
                            setStoryViewerOpenedThisSession(true);
                            sessionStorage.setItem('qrmenu_story_viewer_opened', 'true');
                        }
                    }}
                    onStoryComplete={(storyId) => {
                        handleStoryComplete(storyId);
                        // Mark that viewer was opened this session - NO MORE AUTO OPEN
                        if (!storyViewerOpenedThisSession) {
                            setStoryViewerOpenedThisSession(true);
                            sessionStorage.setItem('qrmenu_story_viewer_opened', 'true');
                        }
                    }}
                    organizationId={organization?.id}
                    visitorId={visitorId}
                />
            )}

            {/* Custom CSS for animations */}
            <style>{`
                @keyframes gradient-x {
                    0%, 100% {
                        background-size: 200% 200%;
                        background-position: left center;
                    }
                    50% {
                        background-size: 200% 200%;
                        background-position: right center;
                    }
                }
                .animate-gradient-x {
                    animation: gradient-x 3s ease infinite;
                }
                
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
                .animate-shimmer {
                    animation: shimmer 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default MenuPage;

